/**
 * Smart Dashboard - Intelligent workspace overview
 * Context-aware, workflow-driven, actually useful
 */

import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { trpc } from '../lib/trpc'
import { useAuthStore } from '../stores/auth-store'
import { useWorkspaceContextStore } from '../stores/workspace-context-store'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Progress } from '../components/ui/progress'
import { Input } from '../components/ui/input'
import { 
  Clock, AlertCircle, CheckCircle2, ArrowRight, 
  Zap, Target, TrendingUp, Users, Calendar,
  Plus, Search, Filter, MoreHorizontal,
  Brain, Flame, Timer, Award, Activity
} from 'lucide-react'
import { cn } from '../lib/utils'
import { formatDistanceToNow } from 'date-fns'

interface SmartAction {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  action: () => void
  urgency: 'high' | 'medium' | 'low'
  context?: string
}

interface WorkflowInsight {
  type: 'bottleneck' | 'opportunity' | 'success' | 'warning'
  title: string
  description: string
  action?: string
  value?: number
}

export default function SmartDashboard() {
  const { workspaceId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { setActiveContext } = useWorkspaceContextStore()
  const [quickAction, setQuickAction] = useState('')
  
  // Core data
  const { data: workspace } = trpc.workspace.get.useQuery(
    { id: workspaceId! },
    { enabled: !!workspaceId }
  )
  
  const { data: tasks = [] } = trpc.task.list.useQuery(
    { workspaceId: workspaceId! },
    { enabled: !!workspaceId }
  )
  
  const { data: recentActivity = [] } = trpc.activity.list.useQuery(
    { workspaceId: workspaceId!, limit: 50 },
    { enabled: !!workspaceId }
  )

  // Smart analytics
  const analytics = useMemo(() => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    
    // Task analysis
    const overdue = tasks.filter(t => t.dueDate && new Date(t.dueDate) < today && t.status !== 'DONE')
    const dueToday = tasks.filter(t => t.dueDate && new Date(t.dueDate).toDateString() === today.toDateString())
    const inProgress = tasks.filter(t => t.status === 'IN_PROGRESS')
    const completedThisWeek = tasks.filter(t => 
      t.status === 'DONE' && t.completedAt && new Date(t.completedAt) >= thisWeek
    )
    
    // User workload
    const myTasks = tasks.filter(t => t.assigneeId === user?.id && t.status !== 'DONE')
    const myOverdue = overdue.filter(t => t.assigneeId === user?.id)
    
    // Team analysis
    const teamActivity = recentActivity.slice(0, 10)
    const stuckTasks = inProgress.filter(t => {
      if (!t.updatedAt) return false
      const lastUpdate = new Date(t.updatedAt)
      return (now.getTime() - lastUpdate.getTime()) > (3 * 24 * 60 * 60 * 1000) // 3 days
    })
    
    return {
      overdue: overdue.length,
      dueToday: dueToday.length,
      inProgress: inProgress.length,
      completedThisWeek: completedThisWeek.length,
      myTasks: myTasks.length,
      myOverdue: myOverdue.length,
      stuckTasks: stuckTasks.length,
      velocity: completedThisWeek.length,
      teamActivity: teamActivity.length,
      focusScore: Math.max(0, 100 - (myOverdue.length * 15) - (stuckTasks.length * 10))
    }
  }, [tasks, recentActivity, user?.id])

  // Smart actions based on current state
  const smartActions = useMemo((): SmartAction[] => {
    const actions: SmartAction[] = []
    
    if (analytics.myOverdue > 0) {
      actions.push({
        id: 'fix-overdue',
        title: `Fix ${analytics.myOverdue} overdue tasks`,
        description: 'These tasks need immediate attention',
        icon: <AlertCircle className="w-4 h-4" />,
        urgency: 'high',
        action: () => {
          // Filter to overdue tasks assigned to user
          navigate(`/workspace/${workspaceId}?filter=overdue-mine`)
        }
      })
    }
    
    if (analytics.stuckTasks > 0) {
      actions.push({
        id: 'unstuck-tasks',
        title: `Unstick ${analytics.stuckTasks} stalled tasks`,
        description: 'Tasks not updated in 3+ days',
        icon: <Timer className="w-4 h-4" />,
        urgency: 'medium',
        action: () => {
          navigate(`/workspace/${workspaceId}?filter=stuck`)
        }
      })
    }
    
    if (analytics.dueToday > 0) {
      actions.push({
        id: 'today-focus',
        title: `Focus on ${analytics.dueToday} tasks due today`,
        description: 'Keep your daily commitments',
        icon: <Target className="w-4 h-4" />,
        urgency: 'high',
        action: () => {
          navigate(`/workspace/${workspaceId}?filter=due-today`)
        }
      })
    }
    
    if (analytics.velocity > 5) {
      actions.push({
        id: 'celebrate',
        title: 'Great week! Share the momentum',
        description: `${analytics.velocity} tasks completed`,
        icon: <Award className="w-4 h-4" />,
        urgency: 'low',
        action: () => {
          navigate(`/workspace/${workspaceId}?view=team-stats`)
        }
      })
    }
    
    return actions.sort((a, b) => {
      const urgencyOrder = { high: 3, medium: 2, low: 1 }
      return urgencyOrder[b.urgency] - urgencyOrder[a.urgency]
    })
  }, [analytics, workspaceId, navigate])

  // Workflow insights
  const insights = useMemo((): WorkflowInsight[] => {
    const insights: WorkflowInsight[] = []
    
    if (analytics.focusScore < 60) {
      insights.push({
        type: 'warning',
        title: 'Focus under pressure',
        description: `Too many urgent items (${analytics.myOverdue + analytics.stuckTasks}). Consider delegating or rescheduling.`,
        action: 'Optimize workload'
      })
    }
    
    if (analytics.velocity > analytics.completedThisWeek * 1.5) {
      insights.push({
        type: 'success',
        title: 'Velocity increasing',
        description: 'Team productivity is trending upward this week.',
        value: analytics.velocity
      })
    }
    
    if (analytics.inProgress > analytics.myTasks * 0.7) {
      insights.push({
        type: 'bottleneck',
        title: 'Too many concurrent tasks',
        description: `${analytics.inProgress} tasks in progress. Consider focusing on fewer items.`,
        action: 'Review priorities'
      })
    }
    
    return insights
  }, [analytics])

  const handleQuickAction = (action: string) => {
    const cmd = action.toLowerCase().trim()
    
    if (cmd.startsWith('goto ')) {
      const target = cmd.replace('goto ', '')
      if (target === 'overdue') navigate(`/workspace/${workspaceId}?filter=overdue-mine`)
      else if (target === 'today') navigate(`/workspace/${workspaceId}?filter=due-today`)
      else if (target === 'team') navigate(`/workspace/${workspaceId}?view=team`)
    } else if (cmd.startsWith('new ')) {
      const title = cmd.replace('new ', '')
      // Quick task creation with intelligent defaults
      navigate(`/workspace/${workspaceId}?create=task&title=${encodeURIComponent(title)}`)
    } else if (cmd === 'focus') {
      navigate(`/workspace/${workspaceId}?mode=focus`)
    }
    
    setQuickAction('')
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {workspace?.name || 'Dashboard'}
            </h1>
            <p className="text-gray-600 mt-1">
              {analytics.focusScore >= 80 ? 'You\'re in the zone' : 
               analytics.focusScore >= 60 ? 'Making good progress' : 
               'Let\'s optimize your workflow'}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Input
              placeholder="Quick action: 'new bug fix', 'goto overdue', 'focus'"
              value={quickAction}
              onChange={(e) => setQuickAction(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleQuickAction(quickAction)}
              className="w-80"
            />
            <Button onClick={() => navigate(`/workspace/${workspaceId}`)}>
              Open Workspace
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-6 gap-4">
          <Card className={cn("border-l-4", analytics.myOverdue > 0 ? "border-l-red-500" : "border-l-gray-200")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">My Overdue</p>
                  <p className="text-2xl font-bold text-red-600">{analytics.myOverdue}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Due Today</p>
                  <p className="text-2xl font-bold text-orange-600">{analytics.dueToday}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">{analytics.inProgress}</p>
                </div>
                <Timer className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">This Week</p>
                  <p className="text-2xl font-bold text-green-600">{analytics.completedThisWeek}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Focus Score</p>
                  <p className="text-2xl font-bold text-purple-600">{analytics.focusScore}</p>
                </div>
                <Brain className="w-8 h-8 text-purple-500" />
              </div>
              <Progress value={analytics.focusScore} className="mt-2" />
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Velocity</p>
                  <p className="text-2xl font-bold text-amber-600">{analytics.velocity}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Smart Actions & Insights */}
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Smart Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {smartActions.length === 0 ? (
                <p className="text-gray-500 text-center py-4">All caught up! 🎉</p>
              ) : (
                smartActions.map((action) => (
                  <div
                    key={action.id}
                    className={cn(
                      "p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors",
                      action.urgency === 'high' && "border-red-200 bg-red-50",
                      action.urgency === 'medium' && "border-yellow-200 bg-yellow-50",
                      action.urgency === 'low' && "border-green-200 bg-green-50"
                    )}
                    onClick={action.action}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {action.icon}
                        <div>
                          <p className="font-medium text-gray-900">{action.title}</p>
                          <p className="text-sm text-gray-600">{action.description}</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-500" />
                Workflow Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {insights.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Workflow running smoothly</p>
              ) : (
                insights.map((insight, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      "p-3 rounded-lg border",
                      insight.type === 'success' && "border-green-200 bg-green-50",
                      insight.type === 'warning' && "border-yellow-200 bg-yellow-50",
                      insight.type === 'bottleneck' && "border-red-200 bg-red-50",
                      insight.type === 'opportunity' && "border-blue-200 bg-blue-50"
                    )}
                  >
                    <p className="font-medium text-gray-900">{insight.title}</p>
                    <p className="text-sm text-gray-600">{insight.description}</p>
                    {insight.action && (
                      <Button size="sm" variant="outline" className="mt-2">
                        {insight.action}
                      </Button>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}